import { useState } from "react";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import GuideSidebar from "@/components/fieldguide/GuideSidebar";
import GuideSection, { GuideSubSection } from "@/components/fieldguide/GuideSection";
import StepList from "@/components/fieldguide/StepList";
import InfoCard from "@/components/fieldguide/InfoCard";
import FeatureCard from "@/components/fieldguide/FeatureCard";
import { PageTransition } from "@/components/PageTransition";
import {
  LayoutDashboard,
  FileText,
  Users,
  Calendar,
  MapPin,
  Clock,
  Settings,
  Radio,
  Download,
  Plus,
  Eye,
} from "lucide-react";

const producerSections = [
  {
    id: "getting-started",
    title: "Getting Started",
    items: [
      { id: "overview", title: "Overview" },
      { id: "create-account", title: "Create an Account" },
      { id: "dashboard", title: "Dashboard Overview" },
    ],
  },
  {
    id: "production-setup",
    title: "Production Setup",
    items: [
      { id: "basic-info", title: "1. Basic Information" },
      { id: "stages", title: "2. Stages per Day" },
      { id: "timetable", title: "3. Timetable Import" },
      { id: "crew", title: "4. The Crew" },
      { id: "map", title: "5. Festival Map" },
      { id: "schedules", title: "6. Crew Schedules" },
      { id: "briefing", title: "7. General Briefing" },
      { id: "tracking-setup", title: "8. Live Tracking Setup" },
      { id: "review-publish", title: "9. Review and Publish" },
    ],
  },
  {
    id: "production-management",
    title: "Production Management",
    items: [
      { id: "production-dashboard", title: "Production Dashboard" },
      { id: "quick-stats", title: "Quick Stats" },
      { id: "pdf-exports", title: "PDF Exports" },
    ],
  },
  {
    id: "crew-management",
    title: "Crew Management",
    items: [
      { id: "invite-crew", title: "Inviting crew members" },
      { id: "delivery-status", title: "Delivery Status" },
      { id: "re-invites", title: "Re-sending Invitations" },
    ],
  },
  {
    id: "schedule-view",
    title: "Schedule View",
    items: [
      { id: "timeline-nav", title: "Timeline Navigation" },
      { id: "day-switching", title: "Day Switching" },
      { id: "export-options", title: "Export Options" },
    ],
  },
  {
    id: "live-tracking",
    title: "Live Tracking",
    items: [
      { id: "map-interface", title: "Map Interface" },
      { id: "crew-locations", title: "Crew Locations" },
      { id: "stage-zones", title: "Stage Zones" },
    ],
  },
  {
    id: "analytics",
    title: "Analytics",
    items: [
      { id: "overview-analytics", title: "Dashboard Overview" },
      { id: "crew-performance", title: "Crew Performance" },
      { id: "stage-coverage", title: "Stage Coverage" },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    items: [
      { id: "profile", title: "Profile Settings" },
      { id: "notifications", title: "Notifications" },
      { id: "emergency-mode", title: "Emergency Mode" },
    ],
  },
];

const ProducerGuide = () => {
  useScrollToHash();
  const [activeSection, setActiveSection] = useState<string>("");

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <div className="flex">
          <GuideSidebar
            sections={producerSections}
            activeSection={activeSection}
            onSectionClick={setActiveSection}
            guideType="producer"
          />

          <main className="max-w-4xl flex-1 p-8 lg:p-12">
            <div className="mb-12">
              <h1 className="mb-4 text-4xl font-bold">Producer guide</h1>
              <p className="text-xl text-muted-foreground">
                Complete documentation for creating and managing productions in PROduce.
              </p>
            </div>

            <div className="space-y-16">
              {/* Getting Started */}
              <GuideSection
                id="getting-started-overview"
                title="Getting Started"
                description="Learn the basics of PROduce and how to set up your first production."
              >
                <GuideSubSection id="getting-started-overview" title="Overview">
                  <p className="mb-4 text-muted-foreground">
                    PROduce is a production management platform designed for live events, festivals,
                    and multi-day productions. As a producer, you can create detailed briefings,
                    manage crew schedules, and track your team in real-time during events.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FeatureCard
                      icon={FileText}
                      title="Production Briefings"
                      description="Create comprehensive briefings with all event details, schedules, and instructions."
                    />
                    <FeatureCard
                      icon={Users}
                      title="Crew Management"
                      description="Add crew members, assign roles, and send automated invitations."
                    />
                    <FeatureCard
                      icon={Calendar}
                      title="Schedule Builder"
                      description="Build visual schedules with drag-and-drop timeline interface."
                    />
                    <FeatureCard
                      icon={Radio}
                      title="Live Tracking"
                      description="Monitor crew locations in real-time during your event."
                    />
                  </div>
                </GuideSubSection>

                <GuideSubSection id="getting-started-create-account" title="Create an Account">
                  <StepList
                    steps={[
                      {
                        title: "Go to the login page",
                        description: "Navigate to the producer login page from the homepage.",
                      },
                      {
                        title: "Click 'Sign Up'",
                        description: "Select the sign up option to create a new account.",
                      },
                      {
                        title: "Enter your details",
                        description: "Provide your email address and create a secure password.",
                      },
                      {
                        title: "Verify your email",
                        description:
                          "Check your inbox and click the verification link to activate your account.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="getting-started-dashboard" title="Dashboard Overview">
                  <p className="mb-4 text-muted-foreground">
                    After logging in, you'll see your producer dashboard. This is your central hub
                    for managing all productions.
                  </p>
                  <div className="space-y-4 rounded-lg border border-border bg-card p-6">
                    <div className="flex items-start gap-4">
                      <LayoutDashboard className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Productions List</h4>
                        <p className="text-sm text-muted-foreground">
                          View all your productions, both active and archived.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Plus className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Create New</h4>
                        <p className="text-sm text-muted-foreground">
                          Start the production setup wizard to create a new event.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Settings className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Settings</h4>
                        <p className="text-sm text-muted-foreground">
                          Access your account settings and preferences.
                        </p>
                      </div>
                    </div>
                  </div>
                </GuideSubSection>
              </GuideSection>

              {/* Production Setup */}
              <GuideSection
                id="production-setup-basic-info"
                title="Production Setup"
                description="A step-by-step guide through the 9-step production wizard."
              >
                <InfoCard variant="info" title="The 9-Step Wizard">
                  Creating a production involves completing 9 steps. You can save your progress at
                  any point and return later to continue where you left off.
                </InfoCard>

                <GuideSubSection id="production-setup-basic-info" title="Step 1: Basic Information">
                  <p className="mb-4 text-muted-foreground">
                    The first step captures essential information about your event.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Festival/Event Name",
                        description:
                          "Enter the official name of your production. This will be displayed to crew members.",
                      },
                      {
                        title: "Client Name (optional)",
                        description:
                          "If you're working for a client, enter their name for reference.",
                      },
                      {
                        title: "Location",
                        description: "Enter the venue or location where the event takes place.",
                      },
                      {
                        title: "Event Dates",
                        description:
                          "Select whether this is a single-day or multi-day event, then set the date(s).",
                      },
                      {
                        title: "Opening Hours",
                        description: "Set the doors open and doors close times for each day.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="production-setup-stages" title="Step 2: Stages per Day">
                  <p className="mb-4 text-muted-foreground">
                    Define all stages or areas where performances or activities will take place.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Add stages",
                        description:
                          "Click 'Add Stage' to create a new stage. Enter the stage name.",
                      },
                      {
                        title: "Configure per day",
                        description:
                          "For multi-day events, specify which stages are active on each day.",
                      },
                      {
                        title: "Copy to other days",
                        description:
                          "Use the copy function to quickly replicate stage setup across days.",
                      },
                    ]}
                  />
                  <InfoCard variant="tip" title="Stage Names">
                    Use clear, recognizable names that match your official event materials. Crew
                    members will reference these names throughout the event.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="production-setup-timetable" title="Step 3: Timetable Import">
                  <p className="mb-4 text-muted-foreground">
                    Import your lineup or timetable to automatically populate artist schedules.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Upload PDF",
                        description: "Upload a PDF containing your lineup or timetable.",
                      },
                      {
                        title: "AI Extraction",
                        description:
                          "PROduce uses AI to extract artist names, stages, and times from the PDF.",
                      },
                      {
                        title: "Review and correct",
                        description: "Check the extracted data and make any necessary corrections.",
                      },
                      {
                        title: "Manual editing",
                        description: "Add, edit, or remove entries as needed.",
                      },
                    ]}
                  />
                  <InfoCard variant="warning" title="PDF Quality">
                    For best results, use clear, text-based PDFs. Scanned images or heavily designed
                    layouts may require more manual correction.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="production-setup-crew" title="Step 4: The Crew">
                  <p className="mb-4 text-muted-foreground">
                    Add all crew members who will be working on this production.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Add crew member",
                        description:
                          "Enter the crew member's name, email address, and assigned function/role.",
                      },
                      {
                        title: "Assign color",
                        description:
                          "Each crew member gets a color for easy identification on schedules and maps.",
                      },
                      {
                        title: "Multi-day setup",
                        description: "Specify which days each crew member will be working.",
                      },
                      {
                        title: "Existing users",
                        description:
                          "If a crew member has used PROduce before, they'll automatically be linked to their existing account.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="production-setup-map" title="Step 5: Festival Map">
                  <p className="mb-4 text-muted-foreground">
                    Configure the event map for navigation and live tracking.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Center the map",
                        description: "Search for your location and center the map on your venue.",
                      },
                      {
                        title: "Draw stage zones",
                        description:
                          "Use the polygon tool to draw boundaries around each stage area.",
                      },
                      {
                        title: "Mark special locations",
                        description:
                          "Add markers for catering, media office, backstage, and other key locations.",
                      },
                      {
                        title: "Configure geofencing",
                        description:
                          "Stage zones are used for live tracking to show which crew members are at which stage.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="production-setup-schedules" title="Step 6: Crew Schedules">
                  <p className="mb-4 text-muted-foreground">
                    Build individual schedules for each crew member using the visual timeline.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Select a crew member",
                        description: "Choose which crew member's schedule you want to edit.",
                      },
                      {
                        title: "Add assignments",
                        description: "Click on the timeline to add a new assignment block.",
                      },
                      {
                        title: "Artist assignments",
                        description: "Link assignments to artists from your imported lineup.",
                      },
                      {
                        title: "Custom tasks",
                        description:
                          "Create custom assignments for non-artist tasks (breaks, setup, etc.).",
                      },
                      {
                        title: "Drag and resize",
                        description: "Adjust timing by dragging blocks or resizing their edges.",
                      },
                    ]}
                  />
                  <InfoCard variant="tip" title="Conflict Detection">
                    PROduce automatically detects scheduling conflicts and will warn you if a crew
                    member is double-booked.
                  </InfoCard>
                </GuideSubSection>

                <GuideSubSection id="production-setup-briefing" title="Step 7: General Briefing">
                  <p className="mb-4 text-muted-foreground">
                    Add all general information that crew members need to know.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-2 font-medium">Catering</h4>
                      <p className="text-sm text-muted-foreground">Meal times and locations</p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-2 font-medium">Dress Code</h4>
                      <p className="text-sm text-muted-foreground">
                        Clothing requirements and guidelines
                      </p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-2 font-medium">Check-in Procedure</h4>
                      <p className="text-sm text-muted-foreground">Where and when to check in</p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-2 font-medium">Equipment</h4>
                      <p className="text-sm text-muted-foreground">Camera and audio settings</p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-2 font-medium">Restrictions</h4>
                      <p className="text-sm text-muted-foreground">Access limitations and rules</p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <h4 className="mb-2 font-medium">General Instructions</h4>
                      <p className="text-sm text-muted-foreground">Any additional notes</p>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection
                  id="production-setup-tracking-setup"
                  title="Step 8: Live Tracking Setup"
                >
                  <p className="mb-4 text-muted-foreground">
                    Configure how GPS tracking will work during your event.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "GPS interval",
                        description:
                          "Choose how frequently crew locations are updated (more frequent = more battery usage).",
                      },
                      {
                        title: "Tracking behavior",
                        description:
                          "Decide if tracking should be active only during assignments or continuously.",
                      },
                      {
                        title: "Privacy considerations",
                        description: "Review the privacy settings that will apply to crew members.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection
                  id="production-setup-review-publish"
                  title="Step 9: Review and Publish"
                >
                  <p className="mb-4 text-muted-foreground">
                    Review all settings and publish your production to make it available to crew
                    members.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Review summary",
                        description: "Check all sections for completeness and accuracy.",
                      },
                      {
                        title: "Preview",
                        description: "See how the briefing will appear to crew members.",
                      },
                      {
                        title: "Publish",
                        description: "Click 'Publish' to finalize and make the production live.",
                      },
                      {
                        title: "Send invitations",
                        description:
                          "After publishing, you can send invitation emails to all crew members.",
                      },
                    ]}
                  />
                  <InfoCard variant="success" title="After Publishing">
                    Once published, crew members can access the briefing through their app. You can
                    still make edits, which will trigger update notifications.
                  </InfoCard>
                </GuideSubSection>
              </GuideSection>

              {/* Production Management */}
              <GuideSection
                id="production-management-production-dashboard"
                title="Production Management"
                description="Manage your production after it's been published."
              >
                <GuideSubSection
                  id="production-management-production-dashboard"
                  title="Production Dashboard"
                >
                  <p className="mb-4 text-muted-foreground">
                    The production dashboard gives you an at-a-glance view of your event's status.
                  </p>
                  <div className="space-y-4 rounded-lg border border-border bg-card p-6">
                    <div className="flex items-start gap-4">
                      <Eye className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Quick Stats</h4>
                        <p className="text-sm text-muted-foreground">
                          View crew count, assignments, and acknowledgment status.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Users className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Crew Widget</h4>
                        <p className="text-sm text-muted-foreground">
                          See crew members and their current status.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Download className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Quick Actions</h4>
                        <p className="text-sm text-muted-foreground">
                          Access common tasks like PDF export and crew management.
                        </p>
                      </div>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="production-management-pdf-exports" title="PDF Exports">
                  <p className="mb-4 text-muted-foreground">
                    Generate PDF documents for offline reference or distribution.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Open export dialog",
                        description:
                          "Click the export button in the production dashboard or schedule view.",
                      },
                      {
                        title: "Choose format",
                        description:
                          "Select between schedule-only, briefing-only, or complete export.",
                      },
                      {
                        title: "Configure options",
                        description:
                          "Choose time format, included sections, and layout preferences.",
                      },
                      { title: "Download", description: "Generate and download the PDF file." },
                    ]}
                  />
                </GuideSubSection>
              </GuideSection>

              {/* Crew Management */}
              <GuideSection
                id="crew-management-invite-crew"
                title="Crew Management"
                description="Manage your crew members and their access to the production."
              >
                <GuideSubSection id="crew-management-invite-crew" title="Inviting crew members">
                  <StepList
                    steps={[
                      {
                        title: "Navigate to Crew page",
                        description:
                          "From the production dashboard, click on 'Crew' in the sidebar.",
                      },
                      {
                        title: "Select crew members",
                        description: "Check the crew members you want to invite.",
                      },
                      {
                        title: "Send invitations",
                        description:
                          "Click 'Send Invitations' to email access instructions to selected crew.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="crew-management-delivery-status" title="Delivery Status">
                  <p className="mb-4 text-muted-foreground">
                    Monitor the delivery status of invitation emails.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-muted-foreground"></div>
                      <span className="text-sm">
                        <strong>Pending</strong> — Invitation not yet sent
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      <span className="text-sm">
                        <strong>Sent</strong> — Email sent, awaiting delivery
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-primary"></div>
                      <span className="text-sm">
                        <strong>Delivered</strong> — Email successfully delivered
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-secondary"></div>
                      <span className="text-sm">
                        <strong>Opened</strong> — Crew member opened the email
                      </span>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="crew-management-re-invites" title="Re-sending Invitations">
                  <p className="mb-4 text-muted-foreground">
                    If a crew member didn't receive their invitation, you can resend it.
                  </p>
                  <InfoCard variant="info">
                    You can resend invitations up to 3 times per crew member. Check the email
                    address is correct before resending.
                  </InfoCard>
                </GuideSubSection>
              </GuideSection>

              {/* Schedule View */}
              <GuideSection
                id="schedule-view-timeline-nav"
                title="Schedule View"
                description="Navigate and manage the production schedule."
              >
                <GuideSubSection id="schedule-view-timeline-nav" title="Timeline Navigation">
                  <p className="mb-4 text-muted-foreground">
                    The schedule view displays all crew assignments on a visual timeline.
                  </p>
                  <StepList
                    steps={[
                      {
                        title: "Zoom controls",
                        description: "Use the zoom slider to adjust the time scale.",
                      },
                      {
                        title: "Scroll",
                        description: "Click and drag to scroll through the timeline.",
                      },
                      {
                        title: "Current time marker",
                        description: "A red line indicates the current time during live events.",
                      },
                    ]}
                  />
                </GuideSubSection>

                <GuideSubSection id="schedule-view-day-switching" title="Day Switching">
                  <p className="mb-4 text-muted-foreground">
                    For multi-day events, switch between days using the day tabs at the top of the
                    schedule.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="schedule-view-export-options" title="Export Options">
                  <p className="mb-4 text-muted-foreground">
                    Export schedules in various formats for offline use or distribution.
                  </p>
                </GuideSubSection>
              </GuideSection>

              {/* Live Tracking */}
              <GuideSection
                id="live-tracking-map-interface"
                title="Live Tracking"
                description="Monitor crew locations in real-time during your event."
              >
                <GuideSubSection id="live-tracking-map-interface" title="Map Interface">
                  <p className="mb-4 text-muted-foreground">
                    The live tracking map shows crew positions updated in real-time.
                  </p>
                  <div className="space-y-4 rounded-lg border border-border bg-card p-6">
                    <div className="flex items-start gap-4">
                      <MapPin className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Crew Markers</h4>
                        <p className="text-sm text-muted-foreground">
                          Each crew member appears as a colored dot with their initials.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Clock className="mt-1 h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-medium">Last Update</h4>
                        <p className="text-sm text-muted-foreground">
                          Shows when each crew member's location was last updated.
                        </p>
                      </div>
                    </div>
                  </div>
                </GuideSubSection>

                <GuideSubSection id="live-tracking-stage-zones" title="Stage Zones">
                  <p className="mb-4 text-muted-foreground">
                    Stage zones you configured during setup appear as highlighted areas on the map.
                    When a crew member enters a stage zone, they're automatically logged as being at
                    that stage.
                  </p>
                </GuideSubSection>
              </GuideSection>

              {/* Analytics */}
              <GuideSection
                id="analytics-overview-analytics"
                title="Analytics"
                description="Review production data and crew performance."
              >
                <GuideSubSection id="analytics-overview-analytics" title="Dashboard Overview">
                  <p className="mb-4 text-muted-foreground">
                    The analytics dashboard provides insights into your production's performance.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="analytics-crew-performance" title="Crew Performance">
                  <p className="mb-4 text-muted-foreground">
                    View statistics on crew activity, GPS reliability, and assignment completion.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="analytics-stage-coverage" title="Stage Coverage">
                  <p className="mb-4 text-muted-foreground">
                    See which stages had crew coverage throughout the event.
                  </p>
                </GuideSubSection>
              </GuideSection>

              {/* Settings */}
              <GuideSection
                id="settings-profile"
                title="Settings"
                description="Configure your account and production preferences."
              >
                <GuideSubSection id="settings-profile" title="Profile Settings">
                  <p className="mb-4 text-muted-foreground">
                    Manage your account information and preferences.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="settings-notifications" title="Notifications">
                  <p className="mb-4 text-muted-foreground">
                    Configure which notifications you receive and how.
                  </p>
                </GuideSubSection>

                <GuideSubSection id="settings-emergency-mode" title="Emergency Mode">
                  <p className="mb-4 text-muted-foreground">
                    Emergency mode allows you to send urgent notifications to all crew members and
                    export emergency calendar files.
                  </p>
                  <InfoCard variant="warning" title="Use Sparingly">
                    Emergency mode should only be used for critical situations. All crew members
                    will receive immediate notifications.
                  </InfoCard>
                </GuideSubSection>
              </GuideSection>
            </div>
          </main>
        </div>
      </div>
    </PageTransition>
  );
};

export default ProducerGuide;
